import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, UserCircle2, LogOut, LayoutGrid } from "lucide-react";
import { Link } from "react-router-dom";
import GlowButton from "@/components/primitives/GlowButton";
import { useAuth } from "@/contexts/AuthContext";

const NAV_LINKS = [
    { label: "The System", href: "/#az-system" },
    { label: "Access", href: "/#access-gates" },
    { label: "Modules", href: "/#expansion-modules" },
    { label: "Smithers", href: "/#smithers" },
    { label: "FAQ", href: "/#faq" },
];

export default function Nav() {
    const [scrolled, setScrolled] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);
    const [authMenuOpen, setAuthMenuOpen] = useState(false);
    const { user, loading, logout, openAuthModal } = useAuth();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        window.addEventListener("scroll", onScroll, { passive: true });
        onScroll();
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        document.body.style.overflow = mobileOpen ? "hidden" : "";
        return () => { document.body.style.overflow = ""; };
    }, [mobileOpen]);

    return (
        <>
            <nav
                data-testid="site-nav"
                className={`
                    fixed top-0 left-0 right-0 z-50
                    transition-all duration-500 ease-out
                    ${scrolled || mobileOpen ? "tca-glass-strong" : "bg-transparent"}
                `}
            >
                <div className="max-w-[1440px] mx-auto px-5 sm:px-8 lg:px-12 h-16 sm:h-[72px] flex items-center justify-between">
                    {/* Logo — stacked on mobile, horizontal on md+ */}
                    <a href="#top" data-testid="nav-logo-link" className="flex items-center gap-3 group">
                        <img
                            src="/brand/logo-stacked.svg"
                            alt="The Creator's Agent"
                            className="md:hidden h-9 w-9 object-contain transition-all duration-500 group-hover:drop-shadow-[0_0_12px_rgba(0,219,255,0.55)]"
                            data-testid="nav-logo-stacked"
                        />
                        <img
                            src="/brand/logo.png"
                            alt="The Creator's Agent"
                            className="hidden md:block h-7 lg:h-8 w-auto object-contain transition-all duration-500 group-hover:drop-shadow-[0_0_12px_rgba(0,219,255,0.55)]"
                            data-testid="nav-logo-horizontal"
                        />
                    </a>

                    {/* Desktop links */}
                    <div className="hidden lg:flex items-center gap-9">
                        {NAV_LINKS.map((link) => (
                            <a
                                key={link.href}
                                href={link.href}
                                data-testid={`nav-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                                className="
                                    relative font-mono-tight text-[11px] uppercase tracking-[0.22em]
                                    text-tca-ink-muted hover:text-tca-cyan-hi transition-colors duration-300
                                "
                            >
                                <span className="relative">
                                    {link.label}
                                    <span className="absolute -bottom-[3px] left-0 w-0 h-px bg-tca-cyan transition-all duration-500 ease-out hover:w-full" />
                                </span>
                            </a>
                        ))}
                    </div>

                    {/* CTA + Mobile toggle */}
                    <div className="flex items-center gap-3">
                        {!loading && user ? (
                            <div className="relative hidden sm:block">
                                <button
                                    type="button"
                                    data-testid="nav-account-chip"
                                    onClick={() => setAuthMenuOpen((s) => !s)}
                                    className="
                                        inline-flex items-center gap-2 px-3 py-2 rounded-full
                                        border border-tca-cyan/35 hover:border-tca-cyan/70
                                        font-mono-tight text-[10px] uppercase tracking-[0.22em]
                                        text-tca-cyan/90 hover:text-tca-cyan-hi
                                        transition-all duration-300
                                    "
                                >
                                    <UserCircle2 size={14} strokeWidth={1.6} />
                                    <span className="max-w-[140px] truncate normal-case tracking-normal text-xs">
                                        {user.email}
                                    </span>
                                </button>
                                <AnimatePresence>
                                    {authMenuOpen && (
                                        <motion.div
                                            initial={{ opacity: 0, y: -6 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            exit={{ opacity: 0, y: -6 }}
                                            transition={{ duration: 0.18 }}
                                            data-testid="nav-account-menu"
                                            className="absolute right-0 mt-2 w-56 border border-tca-cyan/22 bg-tca-bg-panel/95 backdrop-blur-xl rounded-md overflow-hidden"
                                            onMouseLeave={() => setAuthMenuOpen(false)}
                                        >
                                            <Link
                                                to="/portal"
                                                onClick={() => setAuthMenuOpen(false)}
                                                data-testid="nav-portal-link"
                                                className="flex items-center gap-2.5 px-4 py-3 text-sm text-tca-ink hover:text-tca-cyan hover:bg-tca-cyan/5 transition-colors"
                                            >
                                                <LayoutGrid size={13} /> Operator Portal
                                            </Link>
                                            <button
                                                type="button"
                                                onClick={async () => {
                                                    setAuthMenuOpen(false);
                                                    await logout();
                                                }}
                                                data-testid="nav-logout-btn"
                                                className="w-full flex items-center gap-2.5 px-4 py-3 text-sm text-tca-ink-muted hover:text-tca-magenta-accent hover:bg-tca-magenta-accent/5 transition-colors border-t border-tca-ink-dim/30"
                                            >
                                                <LogOut size={13} /> Sign out
                                            </button>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </div>
                        ) : !loading ? (
                            <button
                                type="button"
                                onClick={() => openAuthModal("login")}
                                data-testid="nav-signin-btn"
                                className="
                                    hidden sm:inline-flex items-center font-mono-tight text-[10px] uppercase tracking-[0.22em]
                                    text-tca-ink-muted hover:text-tca-cyan-hi
                                    px-3 py-2 transition-colors duration-300
                                "
                            >
                                Sign in
                            </button>
                        ) : null}

                        <GlowButton
                            size="sm"
                            as="a"
                            href="/#access-gates"
                            data-testid="nav-cta-get-system"
                            className="hidden sm:inline-flex"
                        >
                            Get the System
                        </GlowButton>
                        <button
                            data-testid="nav-mobile-toggle"
                            className="lg:hidden text-tca-ink-muted hover:text-tca-cyan transition-colors p-2"
                            onClick={() => setMobileOpen((s) => !s)}
                            aria-label="Toggle menu"
                            aria-expanded={mobileOpen}
                        >
                            {mobileOpen ? <X size={22} /> : <Menu size={22} />}
                        </button>
                    </div>
                </div>
            </nav>

            <AnimatePresence>
                {mobileOpen && (
                    <>
                        <motion.div
                            key="backdrop"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.25 }}
                            onClick={() => setMobileOpen(false)}
                            className="fixed inset-0 z-40 bg-tca-deep/80 backdrop-blur-sm lg:hidden"
                            data-testid="mobile-menu-backdrop"
                        />
                        <motion.aside
                            key="panel"
                            initial={{ x: "100%" }}
                            animate={{ x: 0 }}
                            exit={{ x: "100%" }}
                            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                            data-testid="mobile-menu-panel"
                            className="fixed top-0 right-0 bottom-0 z-50 w-[88%] max-w-sm bg-tca-bg-panel/95 backdrop-blur-xl border-l border-tca-cyan/15 lg:hidden flex flex-col"
                        >
                            <div className="h-16 sm:h-[72px] flex items-center justify-between px-5 border-b border-tca-ink-dim/30">
                                <span className="font-mono-tight text-[10px] uppercase tracking-[0.32em] text-tca-cyan/85">
                                    // Menu
                                </span>
                                <button
                                    onClick={() => setMobileOpen(false)}
                                    aria-label="Close menu"
                                    data-testid="mobile-menu-close"
                                    className="text-tca-ink-muted hover:text-tca-cyan p-2"
                                >
                                    <X size={22} />
                                </button>
                            </div>

                            <div className="flex-1 overflow-y-auto px-7 py-8 flex flex-col gap-1">
                                {NAV_LINKS.map((link, i) => (
                                    <motion.a
                                        key={link.href}
                                        href={link.href}
                                        onClick={() => setMobileOpen(false)}
                                        data-testid={`mobile-nav-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                                        initial={{ opacity: 0, x: 20 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        transition={{ duration: 0.35, delay: 0.06 * i }}
                                        className="font-display font-semibold text-2xl tracking-[-0.01em] text-tca-ink hover:text-tca-cyan py-4 border-b border-tca-ink-dim/25 transition-colors flex items-center justify-between group"
                                    >
                                        <span>{link.label}</span>
                                        <span className="font-mono-tight text-[10px] tracking-[0.22em] text-tca-cyan/55 group-hover:text-tca-cyan transition-colors">
                                            0{i + 1}
                                        </span>
                                    </motion.a>
                                ))}

                                <motion.div
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.4, delay: 0.4 }}
                                    className="mt-10"
                                >
                                    <GlowButton
                                        size="md"
                                        as="a"
                                        href="#access-gates"
                                        onClick={() => setMobileOpen(false)}
                                        data-testid="mobile-nav-cta"
                                        className="w-full"
                                    >
                                        Get the System
                                    </GlowButton>
                                </motion.div>

                                <div className="mt-auto pt-12">
                                    <div className="font-mono-tight text-[10px] uppercase tracking-[0.32em] text-tca-ink-muted/65">
                                        // QUADRA Core® · v1.0.0
                                    </div>
                                </div>
                            </div>
                        </motion.aside>
                    </>
                )}
            </AnimatePresence>
        </>
    );
}
